import { BonusCondition } from '@synergy/models';
import { ConditionMode, Currency, conditionUnitTranslationMap, currencyTranslationMap } from '@synergy/models';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { FormHelperText } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    FormikPoolDatePicker,
    PoolButton,
    PoolDualButton,
    PoolModal,
    PoolSelect,
    PoolTextArea,
    PoolTextField,
    SelectOption,
} from '@synergy/components';
import { Grid, Theme } from '@mui/material';
import { PoolBanner } from '@synergy/components-next';
import { fields, getSuffixCurrency } from '@synergy/utils';
import { makeStyles } from '@mui/styles';
import { useState } from 'react';

import { bestLocaleSelector } from '../../../../../../common/selectors/locale-selectors';
import { bonusConditionsValidationSchema } from './bonus-conditions-validation-schema';
import { supplierEmployeeSelector } from '../../employees/store/supplier-employee-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        marginTop: theme.spacing(1.5), // FIXME: fix PoolModal container styling
    },
}));

const fieldNames = fields<BonusCondition>();

interface SupplierDetailConditionsBonusConditionModalProps {
    bonusCondition: Partial<BonusCondition> | null;
    onBonusConditionSubmit: (values: BonusCondition) => void;
    onClose: () => void;
}

export const SupplierDetailConditionsBonusConditionModal = (
    props: SupplierDetailConditionsBonusConditionModalProps
) => {
    const { bonusCondition, onBonusConditionSubmit, onClose } = props;
    const classes = useStyles();
    const locale = useAppSelector(bestLocaleSelector);
    const employees = useAppSelector(supplierEmployeeSelector);
    const intl = useIntl();
    const [showBanner, setShowBanner] = useState(true);

    if (!bonusCondition) {
        return null;
    }

    const handleModeOptionChange = (form: FormikProps<BonusCondition>, name: string) => (value: unknown) => {
        if (value) {
            form.setFieldValue(name, value as ConditionMode);
        }
    };

    const currencyOptions: SelectOption[] = currencyTranslationMap.map(currency => ({
        value: currency.value,
        displayValue: <FormattedMessage id={currency.translationKey} />,
    }));

    const handleCurrencyOptionChange = (form: FormikProps<BonusCondition>, name: string) => (value?: string) => {
        if (value) {
            form.setFieldValue(name, value);
        }
    };

    const responsibleContactOptions: SelectOption[] = (employees ?? []).map(employee => ({
        value: employee.id,
        displayValue: employee.fullName,
    }));

    const handleResponsibleContactChange = (form: FormikProps<BonusCondition>, name: string) => (value: unknown) => {
        if (value) {
            form.setFieldValue(name, value as number);

            const responsibleContact = (employees || []).find(employee => employee.id === value);

            if (responsibleContact) {
                form.setFieldValue(fieldNames.responsibleContactEmail, responsibleContact.email);
            }
        }
    };

    const unitOptions: SelectOption[] = conditionUnitTranslationMap.map(unit => ({
        value: unit.value,
        displayValue: <FormattedMessage id={unit.translationKey} />,
    }));

    const handleSubmit = (values: BonusCondition) => {
        onBonusConditionSubmit(values);
    };

    const initialValues: BonusCondition = {
        id: new Date().getTime() * -1,
        title: '',
        responsibleContactId: undefined,
        responsibleContactEmail: undefined,
        mode: ConditionMode.Amount,
        price: undefined,
        conditionFrom: null,
        conditionTo: null,
        internalNote: '',
        description: '',
        percentage: undefined,
        supplierPayout: undefined,
        currency: Currency.EUR,
        quantity: undefined,
        unit: undefined,
        ...bonusCondition,
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={bonusConditionsValidationSchema}
            enableReinitialize={true}
        >
            {formikProps => (
                <PoolModal
                    title={
                        <FormattedMessage
                            id={
                                bonusCondition?.id && bonusCondition.id > 0
                                    ? 'app.contactsAndOrganizations.detail.editCondition'
                                    : 'app.contactsAndOrganizations.detail.addCondition'
                            }
                        />
                    }
                    isVisible={!!bonusCondition}
                    actionChildren={[
                        <PoolButton key="1" styleType="firstLevel" variant="text" onClick={onClose}>
                            <FormattedMessage id="app.common.cancel" />
                        </PoolButton>,
                        <PoolButton
                            key="2"
                            styleType="firstLevel"
                            type="submit"
                            onClick={() => formikProps.submitForm()}
                        >
                            <FormattedMessage id={bonusCondition.id ? 'app.common.save' : 'app.common.add'} />
                        </PoolButton>,
                    ]}
                    handleClose={onClose}
                >
                    <Grid container={true} spacing={5} className={classes.container}>
                        <Grid container={true} item={true} spacing={2}>
                            <Grid item={true} xs={6}>
                                <Field name={fieldNames.title}>
                                    {({ field, meta }: FieldProps<string>) => (
                                        <PoolTextField
                                            label={<FormattedMessage id="app.label.title" />}
                                            required={true}
                                            autoFocus={true}
                                            fullWidth={true}
                                            error={!!meta.touched && !!meta.error}
                                            helperText={
                                                meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                            }
                                            {...field}
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={6}>
                                <Field name={fieldNames.responsibleContactId}>
                                    {({ field, form, meta }: FieldProps<string>) => (
                                        <>
                                            <PoolSelect
                                                label={<FormattedMessage id="app.label.bonusContact" />}
                                                fullWidth={true}
                                                options={responsibleContactOptions}
                                                disallowDefaultValue={true}
                                                onValueChange={handleResponsibleContactChange(form, field.name)}
                                                value={field.value}
                                                name={field.name}
                                                noSorting={true}
                                                required={true}
                                                error={!!meta.error && meta.touched}
                                            />
                                            {meta.error && meta.touched && (
                                                <FormHelperText error={true}>
                                                    <FormattedMessage id={meta.error} />
                                                </FormHelperText>
                                            )}
                                        </>
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={12}>
                                <Field name={fieldNames.responsibleContactEmail}>
                                    {({ field, meta }: FieldProps<string>) => (
                                        <PoolTextField
                                            label={<FormattedMessage id="app.label.email" />}
                                            required={true}
                                            autoFocus={true}
                                            fullWidth={true}
                                            error={!!meta.touched && !!meta.error}
                                            helperText={
                                                meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                            }
                                            {...field}
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={12}>
                                <Field name={fieldNames.description}>
                                    {({ field, meta }: FieldProps<string>) => (
                                        <PoolTextArea
                                            label={<FormattedMessage id="app.label.bonusDescription" />}
                                            fullWidth={true}
                                            rows="3"
                                            rowsMax="50"
                                            placeholder={intl.formatMessage({
                                                id: 'app.contactsAndOrganizations.detail.bonusDescription.placeholder',
                                            })}
                                            error={!!meta.touched && !!meta.error}
                                            helperText={
                                                meta.touched && meta.error ? (
                                                    <FormattedMessage id={meta.error} values={{ value: 300 }} />
                                                ) : null
                                            }
                                            hint={
                                                <FormattedMessage
                                                    id="app.contactsAndOrganizations.detail.bonusDescription.hint"
                                                    values={{ number: field.value ? field.value.length : 0 }}
                                                />
                                            }
                                            {...field}
                                        />
                                    )}
                                </Field>
                            </Grid>
                        </Grid>

                        <Grid container={true} item={true} spacing={2}>
                            <Grid item={true} xs={12} sx={{ mb: 0.5 }}>
                                <Field name={fieldNames.mode}>
                                    {({ field, form }: FieldProps<string>) => (
                                        <PoolDualButton
                                            leftButton={{
                                                value: ConditionMode.Percentage,
                                                message: 'app.contactsAndOrganizations.detail.mode.percentage',
                                            }}
                                            rightButton={{
                                                value: ConditionMode.Amount,
                                                message: 'app.contactsAndOrganizations.detail.mode.amount',
                                            }}
                                            {...field}
                                            onChange={handleModeOptionChange(form, field.name)}
                                        />
                                    )}
                                </Field>
                            </Grid>

                            {formikProps.values.mode === ConditionMode.Percentage && (
                                <Grid item={true} xs={6}>
                                    <Field name={fieldNames.percentage}>
                                        {({ field, meta }: FieldProps<number>) => (
                                            <PoolTextField
                                                label={<FormattedMessage id="app.label.bonusPercentage" />}
                                                fullWidth={true}
                                                fixedDecimalScale={true}
                                                isNumeric={true}
                                                minDecimalScale={2}
                                                noNegativeNumbers={true}
                                                suffix=" %"
                                                error={!!meta.touched && !!meta.error}
                                                helperText={
                                                    meta.touched && meta.error ? (
                                                        <FormattedMessage id={meta.error} />
                                                    ) : null
                                                }
                                                {...field}
                                            />
                                        )}
                                    </Field>
                                </Grid>
                            )}

                            {formikProps.values.mode === ConditionMode.Amount && (
                                <>
                                    <Grid item={true} xs={6}>
                                        <Field name={fieldNames.price}>
                                            {({ field, meta }: FieldProps<number>) => (
                                                <PoolTextField
                                                    label={<FormattedMessage id="app.label.price" />}
                                                    fullWidth={true}
                                                    fixedDecimalScale={true}
                                                    isNumeric={true}
                                                    minDecimalScale={2}
                                                    decimalScale={2}
                                                    noNegativeNumbers={true}
                                                    suffix={
                                                        formikProps.values.currency &&
                                                        getSuffixCurrency(
                                                            field.value,
                                                            Currency[
                                                                formikProps.values.currency as keyof typeof Currency
                                                            ],
                                                            locale
                                                        )
                                                    }
                                                    error={!!meta.touched && !!meta.error}
                                                    helperText={
                                                        meta.touched && meta.error ? (
                                                            <FormattedMessage id={meta.error} />
                                                        ) : null
                                                    }
                                                    {...field}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                    <Grid item={true} xs={6}>
                                        <Field name={fieldNames.currency}>
                                            {({ field, form }: FieldProps<string>) => (
                                                <PoolSelect
                                                    label={<FormattedMessage id="app.label.currency" />}
                                                    fullWidth={true}
                                                    options={currencyOptions}
                                                    disallowDefaultValue={true}
                                                    onValueChange={handleCurrencyOptionChange(form, field.name)}
                                                    value={field.value}
                                                    name={field.name}
                                                    noSorting={true}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                    <Grid item={true} xs={6}>
                                        <Field name={fieldNames.quantity}>
                                            {({ field, meta }: FieldProps<number>) => (
                                                <PoolTextField
                                                    label={<FormattedMessage id="app.label.quantity" />}
                                                    fullWidth={true}
                                                    fixedDecimalScale={true}
                                                    isNumeric={true}
                                                    decimalScale={2}
                                                    noNegativeNumbers={true}
                                                    error={!!meta.touched && !!meta.error}
                                                    helperText={
                                                        meta.touched && meta.error ? (
                                                            <FormattedMessage id={meta.error} />
                                                        ) : null
                                                    }
                                                    {...field}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                    <Grid item={true} xs={6}>
                                        <Field name={fieldNames.unit}>
                                            {({ field, form }: FieldProps<string>) => (
                                                <PoolSelect
                                                    label={<FormattedMessage id="app.label.unit" />}
                                                    fullWidth={true}
                                                    options={unitOptions}
                                                    disallowDefaultValue={true}
                                                    onValueChange={handleCurrencyOptionChange(form, field.name)}
                                                    value={field.value ?? ''}
                                                    name={field.name}
                                                    noSorting={true}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                </>
                            )}

                            <Grid container={true} item={true} rowSpacing={1} columnSpacing={2}>
                                <Grid item={true} xs={6}>
                                    <FormikPoolDatePicker
                                        name={fieldNames.conditionFrom}
                                        label={<FormattedMessage id="app.label.conditionFrom" />}
                                    />
                                </Grid>
                                <Grid item={true} xs={6}>
                                    <FormikPoolDatePicker
                                        name={fieldNames.conditionTo}
                                        label={<FormattedMessage id="app.label.conditionTo" />}
                                    />
                                </Grid>

                                {showBanner && (
                                    <Grid item={true} xs={12} sx={{ pt: 1 }}>
                                        <PoolBanner variant="outlined" onClose={() => setShowBanner(false)}>
                                            <FormattedMessage id="app.contactsAndOrganizations.detail.bonusCondition.banner" />
                                        </PoolBanner>
                                    </Grid>
                                )}
                            </Grid>
                            <Grid container={true} item={true} spacing={2}>
                                <Grid item={true} xs={6}>
                                    <Field name={fieldNames.supplierPayout}>
                                        {({ field, meta }: FieldProps<number>) => (
                                            <PoolTextField
                                                label={
                                                    <FormattedMessage id="app.contactsAndOrganizations.conditions.bonus.supplierPayout" />
                                                }
                                                fullWidth={true}
                                                fixedDecimalScale={true}
                                                isNumeric={true}
                                                minDecimalScale={2}
                                                noNegativeNumbers={true}
                                                suffix=" %"
                                                error={!!meta.touched && !!meta.error}
                                                helperText={
                                                    meta.touched && meta.error ? (
                                                        <FormattedMessage id={meta.error} />
                                                    ) : null
                                                }
                                                {...field}
                                            />
                                        )}
                                    </Field>
                                </Grid>
                            </Grid>
                            <Grid container={true} item={true} spacing={2}>
                                <Grid item={true} xs={12}>
                                    <Field name={fieldNames.internalNote}>
                                        {({ field, meta }: FieldProps<string>) => (
                                            <PoolTextArea
                                                label={<FormattedMessage id="app.label.internalComment" />}
                                                fullWidth={true}
                                                rows="3"
                                                rowsMax="50"
                                                error={!!meta.touched && !!meta.error}
                                                helperText={
                                                    meta.touched && meta.error ? (
                                                        <FormattedMessage id={meta.error} values={{ value: 300 }} />
                                                    ) : null
                                                }
                                                {...field}
                                            />
                                        )}
                                    </Field>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </PoolModal>
            )}
        </Formik>
    );
};
